import { useNavigate } from 'react-router-dom';
import useInView from '../../../hooks/useInView';

const plans = [
  {
    name: 'Essential',
    price: '$9.99',
    period: '/month',
    tagline: 'Basic annual coverage',
    color: 'border-[#E2E8F0]',
    badge: null,
    features: [
      '1 HVAC tune-up per year',
      'Annual safety inspection',
      'Priority scheduling',
      '10% off parts & labor',
      'No overtime charges',
    ],
  },
  {
    name: 'Comfort',
    price: '$19.99',
    period: '/month',
    tagline: 'Most popular for homeowners',
    color: 'border-[#C41E3A]',
    badge: 'Most Popular',
    features: [
      '2 HVAC tune-ups per year',
      'Annual plumbing inspection',
      'Electrical safety check',
      'Priority emergency service',
      '15% off parts & labor',
      'No overtime charges',
      'Transferable to new owner',
    ],
  },
  {
    name: 'Total Home',
    price: '$34.99',
    period: '/month',
    tagline: 'Complete whole-home protection',
    color: 'border-[#1A56A0]',
    badge: 'Best Value',
    features: [
      'Full HVAC, plumbing & electrical',
      'Quarterly system checks',
      'Emergency priority dispatch',
      '20% off all parts & labor',
      'Free water heater inspection',
      'Annual duct cleaning',
      'No overtime charges',
      'Transferable to new owner',
    ],
  },
];

const MembershipSection = () => {
  const navigate = useNavigate();
  const { ref: headerRef, inView: headerInView } = useInView();
  const { ref: plansRef, inView: plansInView } = useInView();
  const { ref: ctaRef, inView: ctaInView } = useInView();

  return (
    <section id="membership" className="py-10 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={headerRef}
          className={`text-center mb-10 md:mb-16 transition-all duration-700 ease-out ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#C41E3A] text-sm font-bold uppercase tracking-widest mb-3">Protection Plans</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#111827] mb-4 sm:mb-5">
            The Renova Plan
          </h2>
          <p className="text-base sm:text-lg text-[#6B7280] max-w-2xl mx-auto">
            Annual protection plans designed to keep your home systems running perfectly. Priority service, preventive maintenance, and exclusive discounts, all in one plan.
          </p>
        </div>

        <div ref={plansRef} className="grid md:grid-cols-3 gap-5 sm:gap-6 mb-10 sm:mb-12">
          {plans.map((p, i) => (
            <div
              key={p.name}
              style={{ transitionDelay: `${i * 120}ms` }}
              className={`relative rounded-2xl border-2 ${p.color} p-8 flex flex-col transition-all duration-700 ease-out ${plansInView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'}`}
            >
              {p.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white ${p.badge === 'Most Popular' ? 'bg-[#C41E3A]' : 'bg-[#1A56A0]'}`}>
                  {p.badge}
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-[#111827] mb-1">{p.name}</h3>
                <p className="text-sm text-[#6B7280] mb-4">{p.tagline}</p>
                <div className="flex items-end space-x-1">
                  <span className="text-4xl font-bold text-[#111827]">{p.price}</span>
                  <span className="text-[#6B7280] mb-1">{p.period}</span>
                </div>
              </div>

              <ul className="space-y-3 flex-1 mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start space-x-3 text-sm text-[#374151]">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#C41E3A] text-base" />
                    </div>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate('/maintenance-plans')}
                className={`w-full py-3 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${p.badge === 'Most Popular' ? 'bg-[#C41E3A] text-white hover:bg-[#A01830]' : p.badge === 'Best Value' ? 'bg-[#1A56A0] text-white hover:bg-[#154A8C]' : 'border border-[#E2E8F0] text-[#374151] hover:bg-[#EEF5FF] hover:border-[#1A56A0]/40'}`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>

        <div
          ref={ctaRef}
          className={`bg-gradient-to-r from-[#0C2340] to-[#1A56A0] rounded-2xl p-6 sm:p-8 md:p-12 text-center transition-all duration-700 ease-out ${ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">Recurring Revenue for Property Managers &amp; HOAs</h3>
          <p className="text-white/70 mb-6 max-w-2xl mx-auto">
            Renova offers custom maintenance contracts for property managers, HOAs, and multi-unit residential clients. One agreement, complete coverage.
          </p>
          <button
            onClick={() => navigate('/commercial')}
            className="bg-white text-[#1A56A0] px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#EEF5FF] transition-colors whitespace-nowrap cursor-pointer"
          >
            View Commercial Plans
          </button>
        </div>
      </div>
    </section>
  );
};

export default MembershipSection;
